/**
 * Icon Registry - Map icon name strings to Ant Design icon components
 */
import React from "react";
import {
  TeamOutlined,
  RocketOutlined,
  UserOutlined,
  BankOutlined,
  DashboardOutlined,
  InboxOutlined,
  SettingOutlined,
  FileOutlined,
  FileTextOutlined,
  ShopOutlined,
  DollarOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
  TagOutlined,
  FolderOutlined,
  ProfileOutlined,
  SolutionOutlined,
  ContactsOutlined,
  ApartmentOutlined,
  QuestionOutlined,
  NotificationOutlined,
  ProjectOutlined,
  CheckSquareOutlined,
  ScheduleOutlined,
  AuditOutlined,
  FormOutlined,
  ContainerOutlined,
  ReconciliationOutlined,
  FundProjectionScreenOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";

type IconComponent = React.ComponentType<{
  className?: string;
  style?: React.CSSProperties;
}>;

/**
 * Map of icon keys to Ant Design icon components
 * Keys are lowercase without "Outlined" suffix
 */
const iconMap: Record<string, IconComponent> = {
  // CRM entities
  team: TeamOutlined,
  rocket: RocketOutlined,
  user: UserOutlined,
  bank: BankOutlined,
  contacts: ContactsOutlined,
  solution: SolutionOutlined,
  apartment: ApartmentOutlined,

  // System
  dashboard: DashboardOutlined,
  inbox: InboxOutlined,
  setting: SettingOutlined,
  profile: ProfileOutlined,

  // Documents
  file: FileOutlined,
  "file-text": FileTextOutlined,
  folder: FolderOutlined,
  form: FormOutlined,
  container: ContainerOutlined,
  audit: AuditOutlined,
  reconciliation: ReconciliationOutlined,

  // Business
  shop: ShopOutlined,
  dollar: DollarOutlined,
  notification: NotificationOutlined,
  project: ProjectOutlined,
  "fund-projection-screen": FundProjectionScreenOutlined,

  // Time & Tasks
  calendar: CalendarOutlined,
  schedule: ScheduleOutlined,
  "check-square": CheckSquareOutlined,

  // Communication
  phone: PhoneOutlined,
  mail: MailOutlined,
  global: GlobalOutlined,

  // Misc
  tag: TagOutlined,
  environment: EnvironmentOutlined,
  appstore: AppstoreOutlined,
  "play-circle": PlayCircleOutlined,
};

/**
 * Get icon component by name
 * @param name Icon name (case-insensitive, without "Outlined" suffix)
 * @returns React icon component
 */
export function getIcon(name: string): IconComponent {
  const normalizedName = name.toLowerCase().replace(/outlined$/i, "");
  return iconMap[normalizedName] || QuestionOutlined;
}

/**
 * Get icon element by name (ready to render)
 * @param name Icon name
 * @returns React element
 */
export function getIconElement(name: string): React.ReactElement {
  const IconComponent = getIcon(name);
  return React.createElement(IconComponent);
}

/**
 * Check if icon name is valid
 */
export function isValidIcon(name: string): boolean {
  const normalizedName = name.toLowerCase().replace(/outlined$/i, "");
  return normalizedName in iconMap;
}

/**
 * Get all available icon names
 */
export function getAvailableIcons(): string[] {
  return Object.keys(iconMap);
}
